<template>
  <div class="dex-confirm-modal-content">
    <div class="dex-title">{{ $t('dexTitle') }}</div>

    <div class="dex-subtitle">{{ $t('dexConfirmOrder') }}</div>

    <div class="dex-you-get">
      <div class="dex-you-get-label">{{ $t('coreYouGet') }}:</div>

      <img :src="data.tokenGetIcon" />

      <div class="dex-you-get-value">{{ data.tokenGetAmount }} {{ data.tokenGetName }}</div>
    </div>

    <hr />

    <div class="dex-you-spend">
      <div class="dex-you-spend-label">{{ $t('coreYouSpend') }}:</div>

      <img
        :src="data.tokenSpendIcon"
        :class="{
          'usdt-icon': data.tokenSpendIcon.includes(StableCoinTokens.usdt)
        }"
      />

      <div class="dex-you-spend-value">{{ data.tokenSpendAmount }} {{ data.tokenSpendName }}</div>
    </div>

    <div class="actual-rate">
      <div class="actual-rate-value">{{ data.rate }}</div>
    </div>

    <div class="collapse-container">
      <el-collapse class="dex-collapse">
        <el-collapse-item :title="$t('coreAdvanced')" :icon="CaretBottom">
          <hr />

          <div class="slippage-tolerance">
            <div class="slippage-tolerance-label">{{ $t('coreSlippageTolerance') }}</div>
            <div class="slippage-tolerance-value">{{ data.slippageTolerance }}</div>
          </div>
        </el-collapse-item>
      </el-collapse>
    </div>

    <button class="button-blue" :disabled="isBuyButtonDisabled" @click="$emit('exchange')">
      {{ confirmButtonLabel }}
    </button>
  </div>
</template>

<script setup lang="ts">
import { CaretBottom } from '@element-plus/icons-vue';
import type { ExchangeConfirmData } from '~/types/dex';
import { StableCoinTokens } from '~/utils/constants';
import { useWeb3ModalAccount } from '@web3modal/ethers/vue';

defineEmits(['exchange']);

const { isEnoughBalance, confirmButtonLabel } = defineProps<{
  data: ExchangeConfirmData;
  isEnoughBalance?: boolean;
  confirmButtonLabel: string;
}>();

const { address } = useWeb3ModalAccount();

const isBuyButtonDisabled = computed(() => (!address.value ? false : !isEnoughBalance));
</script>

<style scoped lang="scss">
.dex-confirm-modal-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  .dex-title {
    font-family: Eczar;
    font-weight: 600;
    font-size: 32px;
    line-height: 1;
    text-align: center;
    color: var(--main-text-color);
    margin-bottom: 0;
  }

  .dex-subtitle {
    font-family: Eczar;
    font-weight: 600;
    font-size: 16px;
    line-height: 100%;
    text-align: center;
    color: var(--dark-6);
    margin-top: 10px;
  }

  .dex-you-get {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;

    .dex-you-get-label {
      font-family: Eczar;
      font-weight: 400;
      font-size: 16px;
      line-height: 100%;
      color: var(--dark-6);
      margin-top: 20px;
      margin-bottom: 10px;
    }

    .dex-you-get-value {
      font-family: Eczar;
      font-weight: 600;
      font-size: 16px;
      line-height: 100%;
      color: var(--main-text-color);
    }

    img {
      height: 50px;
      width: 50px;
      margin-bottom: 10px;
    }
  }

  hr {
    margin: 15px 0 15px;
    padding: 0;
    width: 100%;
    border: none;
    background-color: var(--dark-6);
    height: 1px;
    width: 332px;
  }

  .dex-you-spend {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;

    .dex-you-spend-label {
      font-family: Eczar;
      font-weight: 400;
      font-size: 16px;
      line-height: 100%;
      color: var(--dark-6);
      white-space: nowrap;
    }

    .dex-you-spend-value {
      display: flex;
      align-items: center;
      font-family: Eczar;
      font-weight: 600;
      font-size: 16px;
      line-height: 100%;
      color: var(--main-text-color);
      white-space: nowrap;
    }

    img {
      height: 32px;
      width: 32px;
      margin: 0 10px;
    }

    img:not(.usdt-icon) {
      margin-right: 5px;
    }

    img.usdt-icon {
      width: 32px;
      height: 27px;
    }
  }

  .actual-rate {
    display: flex;
    gap: 10px;
    align-items: center;
    margin-top: 10px;
    margin-bottom: 10px;

    .actual-rate-value {
      font-family: Eczar;
      font-weight: 500;
      font-size: 16px;
      line-height: 100%;
      color: var(--dark-6);
    }

    img {
      width: 32px;
      height: 27px;
    }
  }

  .slippage-tolerance {
    display: flex;
    gap: 10px;
    justify-content: center;
    align-items: center;
    margin-top: 10px;

    .slippage-tolerance-label {
      font-family: Eczar;
      font-weight: 400;
      font-size: 16px;
      line-height: 100%;
      color: var(--dark-6);
    }

    .slippage-tolerance-value {
      font-family: Eczar;
      font-weight: 600;
      font-size: 16px;
      line-height: 100%;
      color: var(--main-text-color);
    }
  }

  .collapse-container {
    margin-bottom: 20px;
  }

  .button-blue {
    padding: 10px 30px;
    min-width: 170px;
    height: 49px;
    background: var(--blue_highlight);
    border-radius: 35px;
    white-space: nowrap;
    box-shadow: 0px 3px 3px 0px var(--box-shadow-1);
    border: solid 1.6px var(--gray-3);
    color: var(--blue-11);
    font-family: Eczar;
    font-weight: 600;
    font-size: 16px;
    line-height: 1;

    &:disabled {
      color: var(--gray-5);
      background-color: var(--gray-4);
    }

    &:hover:not(:disabled) {
      background-color: var(--blue-13);
      color: var(--main-text-color);
      border: solid 1.6px var(--main-text-color);
      transform: scale(1.061);
      transition: transform 0.3s;
    }
  }

  .button-blue:hover,
  .button-blue:active,
  .button-blue:focus {
    outline: none;
    -webkit-tap-highlight-color: transparent;
  }

  .el-collapse {
    background-color: var(--blue-9);
    border: solid 1px var(--blue-8) !important;
    border-radius: 12px;

    .el-collapse-item {
      width: 332px;
      padding: 0 11px;
      font-family: Eczar;
      font-weight: 400;
      font-size: 16px;
      line-height: 100%;
      text-align: center;
      color: var(--dark-6);

      hr {
        height: 2px;
        width: 312px;
        margin: 0;
        background-color: var(--blue-12);
      }
    }

    .el-collapse-item:not(.is-active) {
      height: 27px;
    }

    .el-icon {
      svg {
        display: none;
      }

      &:after {
        content: '';
        background-image: url('~/public/img/icons/CarretBottom.svg');
        background-repeat: no-repeat;
        background-size: contain;
        width: 19px;
        height: 11px;
        min-width: 19px;
      }
    }
  }
}
</style>

<style lang="scss">
.el-collapse.dex-collapse {
  .el-collapse-item.is-active {
    height: 73px;

    .el-collapse-item__content {
      padding-bottom: 5px;
    }
  }
  .el-collapse-item:not(.is-active) {
    height: 27px;

    .el-collapse-item__content {
      display: none;
    }
  }

  .el-collapse-item__wrap {
    background-color: transparent;
    transition: none;
  }

  .el-collapse-item__header {
    background-color: transparent;
    min-height: 27px;
    height: 27px;
    padding-right: 8px;
  }

  .el-collapse-item__title {
    color: var(--dark-6);
    font-weight: 400;
    font-size: 16px;
    width: 100%;
    text-align: center;
    max-height: 27px;
    position: absolute;
    top: -10px;
    left: 50%;
    transform: translateX(-50%);
  }

  .el-icon {
    svg {
      display: none;
    }

    &:after {
      content: '';
      background-image: url('~/public/img/icons/CarretBottom.svg');
      background-repeat: no-repeat;
      background-size: contain;
      width: 12px;
      height: 8px;
      min-width: 12px;
    }
  }

  .el-collapse-item__arrow {
    right: 11px;
    top: 7px;
  }
}
</style>
